import React, { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { LogIn, UserPlus, AlertCircle, ShieldCheck, Smartphone } from 'lucide-react';

const BG_IMAGE = 'https://customer-assets.emergentagent.com/job_scorecard-portal-1/artifacts/a1e8ha1b_Cropped%20Ocean.jpg';
const LOGO = 'https://customer-assets.emergentagent.com/job_scorecard-portal-1/artifacts/llkw5srg_Logo_transparent.png';

export default function LoginPage() {
  const { login, register, verifyTotpSetup, verifyTotp } = useAuth();
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);

  // TOTP states
  const [step, setStep] = useState('credentials'); // 'credentials' | 'setup' | 'verify'
  const [totpEmail, setTotpEmail] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [totpDigits, setTotpDigits] = useState(['', '', '', '', '', '']);
  const [totpLoading, setTotpLoading] = useState(false);
  const inputRefs = useRef([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    setLoading(true);
    try {
      const result = isRegister ? await register(email, password, name) : await login(email, password);
      if (result.pending_approval) {
        setIsRegister(false);
        setPassword('');
        setName('');
        setNotice(result.message || 'Your access request was sent to the admin for approval.');
      } else if (result.requires_totp_setup) {
        setTotpEmail(result.email);
        setTotpSecret(result.totp_secret);
        setQrCode(result.qr_code);
        setMfaToken(result.mfa_token || '');
        setStep('setup');
      } else if (result.requires_totp) {
        setTotpEmail(result.email);
        setMfaToken(result.mfa_token || '');
        setStep('verify');
        setTimeout(() => inputRefs.current[0]?.focus(), 100);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDigitChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const nd = [...totpDigits];
    nd[index] = value.slice(-1);
    setTotpDigits(nd);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
    if (value && index === 5) submitTotp(nd.join(''));
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !totpDigits[index] && index > 0) inputRefs.current[index - 1]?.focus();
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setTotpDigits(pasted.split(''));
      inputRefs.current[5]?.focus();
      setTimeout(() => submitTotp(pasted), 100);
      e.preventDefault();
    }
  };

  const submitTotp = async (code) => {
    const c = code || totpDigits.join('');
    if (c.length < 6) return;
    setTotpLoading(true);
    setError('');
    try {
      if (step === 'setup') {
        await verifyTotpSetup(totpEmail, c, totpSecret, mfaToken);
      } else {
        await verifyTotp(totpEmail, c, mfaToken);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code');
      setTotpDigits(['', '', '', '', '', '']);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } finally {
      setTotpLoading(false);
    }
  };

  const digitInputs = (
    <div className="flex items-center justify-center gap-2" onPaste={handlePaste}>
      {totpDigits.map((digit, i) => (
        <input key={`totp-${i}`} ref={el => inputRefs.current[i] = el} type="text" inputMode="numeric" maxLength={1}
          value={digit} onChange={(e) => handleDigitChange(i, e.target.value)} onKeyDown={(e) => handleKeyDown(i, e)}
          data-testid={`totp-digit-${i}`}
          className="w-11 h-12 text-center font-mono text-xl font-bold bg-secondary/80 border border-border rounded-sm text-foreground focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors"
          disabled={totpLoading} />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen relative flex items-center justify-center" data-testid="login-page">
      <img src={BG_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/60" />

      <div className="relative z-10 w-full max-w-sm mx-4">
        <div className="bg-card/90 backdrop-blur-xl border border-border rounded-sm p-8 shadow-2xl">
          <div className="flex justify-center mb-6">
            <img src={LOGO} alt="OLTA Nachfolge" className="login-logo h-16" />
          </div>

          {step === 'credentials' && (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
              <div className="space-y-1 text-center">
                <h2 className="font-heading text-lg text-foreground">{isRegister ? 'Create Account' : 'Sign In'}</h2>
                <p className="text-xs text-muted-foreground">Restricted to @olta-nachfolge.com emails</p>
              </div>

              {error && <div className="flex items-center gap-2 text-red-400 text-xs bg-red-950/30 border border-red-900/50 rounded-sm p-2"><AlertCircle className="w-3.5 h-3.5 shrink-0" /><span>{error}</span></div>}
              {notice && <div className="flex items-center gap-2 text-emerald-400 text-xs bg-emerald-950/30 border border-emerald-900/50 rounded-sm p-2" data-testid="login-notice"><AlertCircle className="w-3.5 h-3.5 shrink-0" /><span>{notice}</span></div>}

              {isRegister && (
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider">Name</Label>
                  <Input data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Full name" className="bg-secondary/80 border-border text-foreground rounded-sm h-9 text-sm" required />
                </div>
              )}

              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Email</Label>
                <Input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@olta-nachfolge.com" className="bg-secondary/80 border-border text-foreground rounded-sm h-9 text-sm" required />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Password</Label>
                <Input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password" className="bg-secondary/80 border-border text-foreground rounded-sm h-9 text-sm" required minLength={6} />
              </div>

              <Button type="submit" data-testid="login-submit-button" disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-500 text-white rounded-sm font-medium h-9 text-sm">
                {(() => {
                  if (loading) return <span className="flex items-center gap-2"><span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Processing...</span>;
                  if (isRegister) return <span className="flex items-center gap-2"><UserPlus className="w-3.5 h-3.5" /> Create Account</span>;
                  return <span className="flex items-center gap-2"><LogIn className="w-3.5 h-3.5" /> Sign In</span>;
                })()}
              </Button>

              <button onClick={() => { setIsRegister(!isRegister); setError(''); }} data-testid="toggle-auth-mode"
                className="text-xs text-muted-foreground hover:text-foreground transition-colors w-full text-center" type="button">
                {isRegister ? 'Already have an account? Sign in' : "Don't have an account? Create one"}
              </button>
            </form>
          )}

          {step === 'setup' && (
            <div className="space-y-4" data-testid="totp-setup">
              <div className="space-y-1 text-center">
                <div className="flex items-center justify-center gap-2">
                  <Smartphone className="w-5 h-5 text-blue-400" />
                  <h2 className="font-heading text-lg text-foreground">Set Up Authenticator</h2>
                </div>
                <p className="text-xs text-muted-foreground">Scan this QR code with Microsoft Authenticator, Google Authenticator, or any TOTP app</p>
              </div>

              {error && <div className="flex items-center gap-2 text-red-400 text-xs bg-red-950/30 border border-red-900/50 rounded-sm p-2"><AlertCircle className="w-3.5 h-3.5 shrink-0" /><span>{error}</span></div>}

              {qrCode && (
                <div className="flex justify-center">
                  <div className="bg-white p-3 rounded-sm">
                    <img src={`data:image/png;base64,${qrCode}`} alt="QR Code" className="w-48 h-48" data-testid="totp-qr-code" />
                  </div>
                </div>
              )}

              <p className="text-xs text-muted-foreground text-center">Or enter this key manually: <span className="font-mono text-foreground text-[11px] select-all">{totpSecret}</span></p>

              <p className="text-xs text-muted-foreground text-center mt-2">Enter the 6-digit code from your authenticator app to confirm setup:</p>

              {digitInputs}

              <Button onClick={() => submitTotp()} disabled={totpLoading || totpDigits.join('').length < 6} data-testid="verify-setup-button"
                className="w-full bg-blue-600 hover:bg-blue-500 text-white rounded-sm font-medium h-9 text-sm">
                {totpLoading ? <span className="flex items-center gap-2"><span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Verifying...</span>
                  : <span className="flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5" /> Verify & Complete Setup</span>}
              </Button>

              <button onClick={() => { setStep('credentials'); setError(''); }} className="text-xs text-muted-foreground hover:text-foreground transition-colors w-full text-center" type="button">Back</button>
            </div>
          )}

          {step === 'verify' && (
            <div className="space-y-4" data-testid="totp-verify">
              <div className="space-y-1 text-center">
                <div className="flex items-center justify-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-blue-400" />
                  <h2 className="font-heading text-lg text-foreground">Two-Factor Authentication</h2>
                </div>
                <p className="text-xs text-muted-foreground">Enter the 6-digit code from your authenticator app</p>
              </div>

              {error && <div className="flex items-center gap-2 text-red-400 text-xs bg-red-950/30 border border-red-900/50 rounded-sm p-2"><AlertCircle className="w-3.5 h-3.5 shrink-0" /><span>{error}</span></div>}

              {digitInputs}

              <Button onClick={() => submitTotp()} disabled={totpLoading || totpDigits.join('').length < 6} data-testid="verify-totp-button"
                className="w-full bg-blue-600 hover:bg-blue-500 text-white rounded-sm font-medium h-9 text-sm">
                {totpLoading ? <span className="flex items-center gap-2"><span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Verifying...</span>
                  : <span className="flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5" /> Verify</span>}
              </Button>

              <button onClick={() => { setStep('credentials'); setError(''); setTotpDigits(['','','','','','']); }} className="text-xs text-muted-foreground hover:text-foreground transition-colors w-full text-center" type="button">Back to sign in</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
